import '../AccountingEntry.scss'

import { useEffect, useRef, useState } from 'react'

import { Column } from 'primereact/column'
import useHandlerBasicActions from '../../../utils/hooks/useHandlerBasicActions.js'
import useStatePrev from '../../../utils/hooks/useStatePrev.js'

import { useTranslation } from 'react-i18next'
import AppButton from '../../../component/common/AppButton'
import AppDateInput from '../../../component/form/AppDateInput'
import EntityDropdown from '../../../component/form/EntityDropdown'
import AppModal from '../../../component/layout/AppModal'
import AppSection from '../../../component/layout/AppSection'
import AppToolbar from '../../../component/layout/AppToolbar'
import DataTableWorkerProjects from '../../../components/accounting-entry/DataTableWorkerProjects.jsx'
import EditButton from '../../../components/common/buttons/EditButton.jsx'
import DataTableComp from '../../../components/common/data-table/DataTableComp.jsx'
import AccountingEntryService from '../../../services/accounting-entry/accountingEntry.service.js'
import useModal from '../../../utils/hooks/useModal.js'
import AccountingEntryCommonHeader from '../nav/AccountingEntryCommonHeader'
import AccountingEntryAnalyticalDistributionForm from './AccountingEntryAnalyticalDistributionForm'

const AccountingEntryAnalyticalDistribution = () => {
  const { t } = useTranslation()

  const storedCompanyCode = localStorage.getItem('AccountingEntry.companyCode')

  const [companyCode, setCompanyCode] = useState(storedCompanyCode)

  const Views = {
    WorkersAll: 0,
    WorkersWithoutProjects: 1,
  }

  // State
  const { isShowing: isShowingEdit, toggle: toggleEdit } = useModal()
  const [currentRowData, setCurrentRowData] = useState(null)
  const [currentView, setCurrentView, prevCurrentView] = useStatePrev(
    Views.WorkersAll,
  )
  const storedPeriod = localStorage.getItem('AccountingEntry.period') || null
  const [period, setPeriod, prevPeriod] = useStatePrev(storedPeriod)
  const [year, setYear] = useState(
    new Date(period).getFullYear() > 1970
      ? new Date(period).getFullYear()
      : null,
  )
  const [month, setMonth] = useState(
    new Date(period).getFullYear() > 1970
      ? new Date(period).getMonth() + 1
      : null,
  )

  // Previous
  const prevCompanyCode = useRef(companyCode)
  const prevIsShowingEdit = useRef(isShowingEdit)

  useEffect(() => {
    setCompanyCode(storedCompanyCode || null)
  }, [storedCompanyCode])

  useEffect(() => {
    if (prevCompanyCode.current !== companyCode)
      dataFacade[currentView].reload()
    prevCompanyCode.current = companyCode
  }, [companyCode])
  useEffect(() => {
    if (prevPeriod.current !== period) dataFacade[currentView].reload()
    prevPeriod.current = period
  }, [year, month])
  useEffect(() => {
    if (prevCurrentView.current !== currentView)
      dataFacade[currentView].reload()
    prevCurrentView.current = currentView
  }, [currentView])
  useEffect(() => {
    if (prevIsShowingEdit.current !== isShowingEdit && !isShowingEdit)
      dataFacade[currentView].reload()
    prevIsShowingEdit.current = isShowingEdit
  }, [isShowingEdit])
  useEffect(() => {
    setYear(
      new Date(period).getFullYear() > 1970
        ? new Date(period).getFullYear()
        : null,
    )
    setMonth(
      new Date(period).getFullYear() > 1970
        ? new Date(period).getMonth() + 1
        : null,
    )
  }, [period])

  const buttonEditProjects = (rowData) => (
    <EditButton onClick={() => handleEditProjects(rowData)} />
  )

  const handlePeriodChange = (e) => {
    setPeriod(e.target.value)
    localStorage.setItem('AccountingEntry.period', e.target.value)
  }

  const handleCompanyCodeChange = (value) => {
    setCompanyCode(value)
    localStorage.setItem('AccountingEntry.companyCode', value)
  }

  const handleEditProjects = (rowData) => {
    setCurrentRowData(null)
    if (rowData && rowData.workerCode) {
      setCurrentRowData(rowData)
    }
    toggleEdit()
  }

  const changeView = () => {
    setCurrentView(
      currentView === Views.WorkersAll
        ? Views.WorkersWithoutProjects
        : Views.WorkersAll,
    )
  }

  const retrieveWorkers = ({ filter, sort, limit, skip }) => {
    return AccountingEntryService.workers.getWorkers({
      companyCode,
      year,
      month,
      filter,
      sort,
      limit,
      skip,
    })
  }
  const retrieveWorkersWithoutProjects = ({ filter, sort, limit, skip }) => {
    return AccountingEntryService.workers.getWorkersWithoutProject({
      companyCode,
      year,
      month,
      filter,
      sort,
      limit,
      skip,
    })
  }

  const dataFacade = [
    useHandlerBasicActions({
      getAll: ({ filter, sort, limit, skip }) =>
        retrieveWorkers({ filter, sort, limit, skip }),
    }),
    useHandlerBasicActions({
      getAll: ({ filter, sort, limit, skip }) =>
        retrieveWorkersWithoutProjects({ filter, sort, limit, skip }),
    }),
  ]

  const pagerOptions = {
    ...dataFacade[currentView]?.pagerOptions,
    totalRecords: dataFacade[currentView]?.data?.total,
  }
  const renderSubstitution = (data) =>
    data.isSubstitution ? (
      <span className="p-badge-custom bg-success">{t('button.yes')}</span>
    ) : (
      <span className="p-badge-custom text-danger">{t('button.no')}</span>
    )

  const renderProjects = (data) =>
    data && data.projects && <DataTableWorkerProjects data={data.projects} />

  const dataTable =
    companyCode !== null &&
    companyCode !== undefined &&
    dataFacade[currentView]?.data?.resp ? (
      <DataTableComp
        loading={dataFacade?.loading}
        pagerOptions={pagerOptions}
        tableOptions={{
          className: 'table-analytical-distribution-menu',
          value: dataFacade[currentView]?.data?.resp,
          dataKey: 'workerCode',
          lazy: true,
          ...dataFacade[currentView]?.sortOptions,
          sortable: false,
        }}
        autoColumnKey
        columns={
          currentView === Views.WorkersAll
            ? [
                <Column
                  key="workerCode"
                  className="text-monospace"
                  field="workerCode"
                  style={{ width: '10%' }}
                  header={t('dni')}
                  sortable
                />,
                <Column
                  field="workerName"
                  style={{ width: '30%' }}
                  header={t('worker')}
                  sortable
                />,
                <Column
                  field="isSubstitution"
                  style={{ width: '10%' }}
                  align="center"
                  header={t('substitutions')}
                  body={(rowData) => renderSubstitution(rowData)}
                  sortable
                />,
                <Column
                  field="projects"
                  style={{ width: '40%' }}
                  header={t('projects')}
                  body={(rowData) => renderProjects(rowData)}
                />,
                <Column
                  field="_id"
                  align="right"
                  body={(rowData) => buttonEditProjects(rowData)}
                  exportable={false}
                />,
              ]
            : [
                <Column
                  key="workerCode"
                  className="text-monospace"
                  field="workerCode"
                  style={{ width: '10%' }}
                  header={t('dni')}
                  sortable
                />,
                <Column
                  field="workerName"
                  style={{ width: '70%' }}
                  header={t('worker')}
                  sortable
                />,
                <Column
                  field="isSubstitution"
                  style={{ width: '10%' }}
                  align="center"
                  header={t('substitutions')}
                  body={(rowData) => renderSubstitution(rowData)}
                />,
                <Column header={t('projects')} />,
                <Column
                  field="_id"
                  body={(rowData) => buttonEditProjects(rowData)}
                  exportable={false}
                />,
              ]
        }
      />
    ) : null

  // const headerTitle = () => {
  //     if (!dataFacade[currentView].loading && (companyCode === null || companyCode === undefined)) return msg({ id: "accountingEntry.noParam" })
  //     return (
  //         <>
  //             <span>{msg({ id: "period" })}</span>
  //             <PeriodSelector disabled={dataFacade[currentView]?.loading} value={period} onChange={handlePeriodChange} />
  //             <span className="opacity-25">{dataFacade[currentView]?.loading && "..."}</span>
  //         </>
  //     )
  // }

  // const headerOptions = {
  //     title: headerTitle(),
  //     handlerHeaderActions: {
  //         primaryButton: changeView,
  //         primaryButtonText: currentView === Views.WorkersAll ? "seeWorkersWithoutProject" : "seeWorkersAll",
  //         primaryButtonIcon: "pi-eye",

  //         // isDisabledSecondaryButton: dataFacade[currentView]?.loading || dataFacade[currentView]?.data?.total <= 0,
  //         secondaryButton: handleEditProjects,
  //         secondaryButtonText: "addProjects",
  //         secondaryButtonIcon: "pi-plus",
  //     },
  //     searchOptions: dataFacade[currentView]?.data?.resp && dataFacade[currentView]?.searchOptions,
  // }

  return (
    <AppSection
      header={<AccountingEntryCommonHeader />}
      subheader={
        <AppToolbar
          leftSection={[
            <EntityDropdown
              // miw={280}
              // labelh="company"
              entity="accountingEntryCompany"
              value={companyCode}
              onChange={({ value }) => handleCompanyCodeChange(value)}
            />,
            <AppDateInput
              disabled={dataFacade[currentView].loading}
              value={period}
              onChange={handlePeriodChange}
            />,
          ]}
          rightSection={[
            <AppButton
              entity="eye"
              label={
                currentView === Views.WorkersAll
                  ? 'seeWorkersWithoutProject'
                  : 'seeWorkersAll'
              }
              onClick={changeView}
            />,
            <AppButton
              entity="plus"
              bg="moure-orange"
              label="addProjects"
              onClick={handleEditProjects}
            />,
          ]}
        />
      }
    >
      {dataTable}
      <AppModal opened={isShowingEdit} close={toggleEdit} title={t('projects')}>
        <AccountingEntryAnalyticalDistributionForm
          companyCode={companyCode}
          currentRowData={currentRowData}
          hide={toggleEdit}
        />
      </AppModal>
    </AppSection>
  )
}

export default AccountingEntryAnalyticalDistribution

// const AccountingEntryAnalyticalDistribution2 = () => {
//     const { formatMessage: msg } = useIntl()

//     const storedCompanyCode = Number(localStorage.getItem("AccountingEntry.companyCode"))
//     const [ companyCode, setCompanyCode ] = useState(storedCompanyCode)

//     const Views = {
//         WorkersAll: 0,
//         WorkersWithoutProjects: 1,
//     }

//     // State
//     const { isShowing: isShowingEdit, toggle: toggleEdit } = useModal()
//     const [currentRowData, setCurrentRowData] = useState(null)
//     const [currentView, setCurrentView, prevCurrentView] = useStatePrev(Views.WorkersAll)
//     const storedPeriod = localStorage.getItem("AccountingEntry.period") || null
//     const [period, setPeriod, prevPeriod] = useStatePrev(storedPeriod)
//     const [year, setYear] = useState(new Date(period).getFullYear() > 1970 ? new Date(period).getFullYear() : null)
//     const [month, setMonth] = useState(new Date(period).getFullYear() > 1970 ? new Date(period).getMonth() + 1 : null)

//     // Previous
//     const prevCompanyCode = useRef(companyCode)
//     const prevIsShowingEdit = useRef(isShowingEdit)

//     useEffect(() => {
//         if (prevCompanyCode.current !== companyCode) dataFacade[currentView].reload()
//         prevCompanyCode.current = companyCode
//     }, [companyCode])
//     useEffect(() => {
//         if (prevPeriod.current !== period) dataFacade[currentView].reload()
//         prevPeriod.current = period
//     }, [year, month])
//     useEffect(() => {
//         if (prevCurrentView.current !== currentView) dataFacade[currentView].reload()
//         prevCurrentView.current = currentView
//     }, [currentView])
//     useEffect(() => {
//         if (prevIsShowingEdit.current !== isShowingEdit && !isShowingEdit) dataFacade[currentView].reload()
//         prevIsShowingEdit.current = isShowingEdit
//     }, [isShowingEdit])
//     useEffect(() => {
//         setYear(new Date(period).getFullYear() > 1970 ? new Date(period).getFullYear() : null)
//         setMonth(new Date(period).getFullYear() > 1970 ? new Date(period).getMonth() + 1 : null)
//     }, [period])

//     const buttonEditProjects = (rowData) => <EditButton onClick={() => handleEditProjects(rowData)} />

//     const handlePeriodChange = (e) => {
//         setPeriod(e.target.value)
//         localStorage.setItem("AccountingEntry.period", e.target.value)
//     }

//     const handleEditProjects = (rowData) => {
//         setCurrentRowData(null)
//         if (rowData && rowData.workerCode) {
//             setCurrentRowData(rowData)
//         }
//         toggleEdit()
//     }

//     const changeView = () => {
//         setCurrentView(currentView === Views.WorkersAll ? Views.WorkersWithoutProjects : Views.WorkersAll)
//     }

//     const retrieveWorkers = ({filter, sort, limit, skip}) => {
//         return AccountingEntryService.workers.getWorkers({companyCode, year, month, filter, sort, limit, skip})
//     }
//     const retrieveWorkersWithoutProjects = ({filter, sort, limit, skip}) => {
//         return AccountingEntryService.workers.getWorkersWithoutProject({companyCode, year, month, filter, sort, limit, skip})
//     }

//     const dataFacade = [
//         useHandlerBasicActions({
//             getAll: ({ filter, sort, limit, skip }) => retrieveWorkers({filter, sort, limit, skip}),
//         }),
//         useHandlerBasicActions({
//             getAll: ({ filter, sort, limit, skip }) => retrieveWorkersWithoutProjects({filter, sort, limit, skip}),
//         }),
//     ]

//     const pagerOptions = {
//         ...dataFacade[currentView]?.pagerOptions,
//         totalRecords: dataFacade[currentView]?.data?.total,
//     }
//     const renderSubstitution = (data) => (data.isSubstitution ? <span className="p-badge-custom bg-success">{msg({ id: "button.yes" })}</span> : <span className="p-badge-custom text-danger">{msg({ id: "button.no" })}</span>)

//     const renderProjects = (data) => (
//         data && data.projects && (
//             <DataTableWorkerProjects
//                 data={data.projects}
//             />
//         )
//     )

//     const dataTable = companyCode !== null && companyCode !== undefined && dataFacade[currentView]?.data?.resp ? (
//         <DataTableComp
//             pagerOptions={pagerOptions[currentView]}
//             tableOptions={{
//                 className: "table-analytical-distribution-menu",
//                 value: dataFacade[currentView]?.data?.resp,
//                 dataKey: "workerCode",
//                 lazy: true,
//                 ...dataFacade[currentView]?.sortOptions,
//                 sortable: false,
//             }}
//             autoColumnKey
//             columns={
//                 currentView === Views.WorkersAll
//                     ? [
//                         <Column key="workerCode" className="text-monospace" field="workerCode" style={{ width: "10%" }} header={msg({ id: "dni" })} sortable />,
//                         <Column field="workerName" style={{ width: "30%" }} header={msg({ id: "worker" })} sortable />,
//                         <Column field="isSubstitution" style={{ width: "10%" }} align="center" header={msg({ id: "substitutions" })} body={(rowData) => renderSubstitution(rowData)} sortable/>,
//                         <Column field="projects" style={{ width: "40%" }} header={msg({ id: "projects" })} body={(rowData) => renderProjects(rowData)} />,
//                         <Column field="_id" align="right" body={(rowData) => buttonEditProjects(rowData)} exportable={false} />,
//                     ]
//                     : [
//                         <Column key="workerCode" className="text-monospace" field="workerCode" style={{ width: "10%" }} header={msg({ id: "dni" })} sortable />,
//                         <Column field="workerName" style={{ width: "70%" }} header={msg({ id: "worker" })} sortable />,
//                         <Column field="isSubstitution" style={{ width: "10%" }} align="center" header={msg({ id: "substitutions" })} body={(rowData) => renderSubstitution(rowData)} />,
//                         <Column header={msg({ id: "projects" })} />,
//                         <Column field="_id" body={(rowData) => buttonEditProjects(rowData)} exportable={false} />,
//                     ]
//             }
//         />
//     ) : null

//     // const headerTitle = () => {
//     //     if (!dataFacade[currentView].loading && (companyCode === null || companyCode === undefined)) return msg({ id: "accountingEntry.noParam" })
//     //     return (
//     //         <>
//     //             <span>{msg({ id: "period" })}</span>
//     //             <PeriodSelector disabled={dataFacade[currentView]?.loading} value={period} onChange={handlePeriodChange} />
//     //             <span className="opacity-25">{dataFacade[currentView]?.loading && "..."}</span>
//     //         </>
//     //     )
//     // }

//     // const headerOptions = {
//     //     title: headerTitle(),
//     //     handlerHeaderActions: {
//     //         primaryButton: changeView,
//     //         primaryButtonText: currentView === Views.WorkersAll ? "seeWorkersWithoutProject" : "seeWorkersAll",
//     //         primaryButtonIcon: "pi-eye",

//     //         // isDisabledSecondaryButton: dataFacade[currentView]?.loading || dataFacade[currentView]?.data?.total <= 0,
//     //         secondaryButton: handleEditProjects,
//     //         secondaryButtonText: "addProjects",
//     //         secondaryButtonIcon: "pi-plus",
//     //     },
//     //     searchOptions: dataFacade[currentView]?.data?.resp && dataFacade[currentView]?.searchOptions,
//     // }

//     return (
//         <AppSection
//             header={(<AccountingEntryCommonHeader />)}
//         >
//             {dataTable}
//             <Modal isShowing={isShowingEdit} hide={toggleEdit} title={msg({ id: "projects" })}
//                 width="80%"
//                 context={
//                     <AccountingEntryAnalyticalDistributionForm companyCode={companyCode} currentRowData={currentRowData} hide={toggleEdit} />
//                 }
//             />
//         </AppSection>
//     )

// }

// // return (
// //     <div className="accounting-entry-analytical-distribution-container">
// //         <MainCommonView headerOptionsV2={headerOptions} componentData={dataTable} pagerOptions={pagerOptions} />
// //         <Modal isShowing={isShowingEdit} hide={toggleEdit} title={msg({ id: "projects" })}
// //             width="80%"
// //             context={
// //                 <AccountingEntryAnalyticalDistributionForm companyCode={companyCode} currentRowData={currentRowData} hide={toggleEdit} />
// //             }
// //         />
// //     </div>
// // )
